import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import StyledDialogTitle from "../../../common/components/dialog/dialog-title";
import StyledDialogActions from "../../../common/components/dialog/dialog-actions";
import { commonButtonProps } from "../../../common/components/buttons/sxprops";
import { ModalTransition } from "../../../common/components/transitions";
import useUserManagementStore from "../api/store";
import {
  useGetInternalProducts,
  useGetAssignedProducts,
  useAssignProducts,
} from "../api/hooks";

export const AssignInternalProductsModal: React.FC = () => {
  const { isAssignProductsModalOpen, setAssignProductsModal, activeUser } =
    useUserManagementStore();
  const isTargetSuperAdmin = activeUser?.role === "SuperAdmin";

  const userId = isAssignProductsModalOpen ? (activeUser?.applicationUserId ?? null) : null;

  const { data: internalProducts, isLoading: loadingProducts } = useGetInternalProducts();
  const { data: assigned, isLoading: loadingAssigned } = useGetAssignedProducts(userId);
  const { mutate: assignProducts, isPending } = useAssignProducts();

  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    if (assigned) {
      setSelected(assigned.productIds);
    }
  }, [assigned]);

  useEffect(() => {
    if (!isAssignProductsModalOpen) {
      setSelected([]);
    }
  }, [isAssignProductsModalOpen]);

  const handleToggle = (productId: number) => {
    if (isTargetSuperAdmin) return;
    setSelected((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  };

  const handleSave = () => {
    if (!userId) return;
    assignProducts({ userId, productIds: selected });
  };

  const isLoading = loadingProducts || loadingAssigned;
  const products = internalProducts ?? [];

  return (
    <Dialog
      open={isAssignProductsModalOpen}
      onClose={() => setAssignProductsModal(false)}
      slots={{ transition: ModalTransition }}
      slotProps={{ paper: { sx: { borderRadius: "24px" } } }}
      maxWidth="laptop"
      fullWidth
      aria-describedby="assign-products-dialog-description"
    >
      <StyledDialogTitle
        actionType="close"
        title="Manage Internal Products"
        subtitle="Select the internal products this user can access."
        handleClose={() => setAssignProductsModal(false)}
      />

      <DialogContent sx={{ minHeight: 200 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : products.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No internal products found.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {products.map((product) => (
              <Box
                key={product.id}
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ p: 1, borderRadius: 1, "&:hover": { bgcolor: "grey.50" } }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isTargetSuperAdmin ? true : selected.includes(product.id)}
                      disabled={isTargetSuperAdmin}
                      onChange={() => handleToggle(product.id)}
                    />
                  }
                  label={product.productName}
                  sx={{ flex: 1, m: 0 }}
                />
                <Chip
                  label="Internal"
                  size="small"
                  sx={{
                    bgcolor: "rgba(106, 27, 154, 0.1)",
                    color: "#6A1B9A",
                    fontSize: "10px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <StyledDialogActions>
        <Button onClick={() => setAssignProductsModal(false)}>
          {isTargetSuperAdmin ? "Close" : "Cancel"}
        </Button>
        {!isTargetSuperAdmin && (
          <LoadingButton
            onClick={handleSave}
            loading={isPending}
            loadingIndicator={<CircularProgress size={18} sx={{ color: "white" }} />}
            disabled={isPending}
            sx={commonButtonProps}
          >
            Save Changes
          </LoadingButton>
        )}
      </StyledDialogActions>
    </Dialog>
  );
};
